import React, { useEffect, useState } from "react";
import { Workflow, Check, Languages, Save } from "lucide-react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CmsTextEditor from "../../components/cms/CmsTextEditor";
import apiClient from "../../api/apiClient";
import { create_faq_question } from "../../reduxData/cms/cmsAction";
import "./components/bilingualCms.css";

const isEmptyHtml = (html = "") => {
  const node = document.createElement("div");
  node.innerHTML = html;
  return !node.textContent.trim();
};

export default function HowItWorks() {
  const { t } = useTranslation();
  const type = "how_it_work";
  const title = t("cmsContent.howItWorks.title");
  const description = t("cmsContent.howItWorks.description");
  const Icon = Workflow;
  const withHeading = false;
  const headingLabel = t("cmsContent.editor.heading");
  const contentLabel = t("cmsContent.howItWorks.contentLabel");

  const dispatch = useDispatch();
  const { userPermissions } = useSelector((state) => state.auth);
  const canEdit = userPermissions?.cms?.edit === true;
  const canToggle = userPermissions?.cms?.delete === true;
  const [activeLanguage, setActiveLanguage] = useState("EN");
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState({ heading: "", heading_nl: "", content: "", content_nl: "", status: false });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`cms/content/${type}`);
      const item = response.data?.data ?? null;
      setRecord(item);
      const content = item?.content ?? {};
      setForm({
        heading: content.heading ?? "",
        heading_nl: content.heading_nl ?? "",
        content: content.content ?? content.Content ?? "",
        content_nl: content.content_nl ?? content.Content_nl ?? "",
        status: item?.status === true || item?.status === 1 || item?.status === "1" || item?.status === "true",
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || t("cmsContent.editor.loadError", { title }));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [type]);

  const languageSuffix = activeLanguage === "NL" ? "_nl" : "";
  const contentKey = `content${languageSuffix}`;
  const headingKey = `heading${languageSuffix}`;
  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };
  const submit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    ["content", "content_nl"].forEach((key) => { if (isEmptyHtml(form[key])) nextErrors[key] = t("cmsContent.editor.contentRequired", { language: t(key.endsWith("_nl") ? "cmsContent.languages.dutch" : "cmsContent.languages.english") }); });
    if (withHeading) ["heading", "heading_nl"].forEach((key) => { if (!form[key].trim()) nextErrors[key] = t("cmsContent.editor.headingRequired", { language: t(key.endsWith("_nl") ? "cmsContent.languages.dutch" : "cmsContent.languages.english") }); });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) { setActiveLanguage(Object.keys(nextErrors)[0].endsWith("_nl") ? "NL" : "EN"); return; }
    setSaving(true);
    try {
      const content = { content: form.content, content_nl: form.content_nl };
      if (withHeading) Object.assign(content, { heading: form.heading, heading_nl: form.heading_nl });
      await create_faq_question({ name: type, type, status: form.status, content }, record?.id, dispatch);
      toast.success(t("cmsContent.editor.saved", { title }));
      await load();
    } finally { setSaving(false); }
  };

  return <main className="bcms-page">
    <header className="bcms-header"><div className="bcms-header__icon"><Icon size={25} /></div><div><span>{t("cmsContent.editor.eyebrow")}</span><h1>{title}</h1><p>{description}</p></div><div className={`bcms-state ${form.status ? "is-live" : ""}`}><i />{t(form.status ? "cmsContent.editor.published" : "cmsContent.editor.hidden")}</div></header>
    <form className="bcms-card" onSubmit={submit}>
      <div className="bcms-card__top"><div><h2>{t("cmsContent.editor.localizedContent")}</h2><p>{t("cmsContent.editor.localizedHint")}</p></div>
        <div className="bcms-tabs" role="tablist">{["EN", "NL"].map((language) => <button type="button" role="tab" aria-selected={activeLanguage === language} className={activeLanguage === language ? "active" : ""} onClick={() => setActiveLanguage(language)} key={language}><span>{language}</span>{t(language === "EN" ? "cmsContent.languages.english" : "cmsContent.languages.dutch")}{!isEmptyHtml(form[`content${language === "NL" ? "_nl" : ""}`]) && <Check size={14} />}</button>)}</div>
      </div>
      {loading ? <div className="bcms-loading"><Spinner animation="border" /><span>{t("cmsContent.editor.loading")}</span></div> : <div className="bcms-editor-panel">
        <div className="bcms-language-label"><Languages size={17} /><strong>{t(activeLanguage === "EN" ? "cmsContent.editor.englishContent" : "cmsContent.editor.dutchContent")}</strong><span>{activeLanguage}</span></div>
        {withHeading && <label className="bcms-field"><span>{headingLabel} *</span><input className={errors[headingKey] ? "form-control is-invalid" : "form-control"} value={form[headingKey]} disabled={!canEdit} placeholder={t(activeLanguage === "EN" ? "cmsContent.editor.englishHeadingPlaceholder" : "cmsContent.editor.dutchHeadingPlaceholder")} onChange={(e) => update(headingKey, e.target.value)} />{errors[headingKey] && <small>{errors[headingKey]}</small>}</label>}
        <label className="bcms-field"><span>{contentLabel} *</span><div className={errors[contentKey] ? "bcms-quill has-error" : "bcms-quill"}><CmsTextEditor value={form[contentKey]} disabled={!canEdit} onChange={(value) => update(contentKey, value)} placeholder={t(activeLanguage === "EN" ? "cmsContent.editor.englishContentPlaceholder" : "cmsContent.editor.dutchContentPlaceholder")} /></div>{errors[contentKey] && <small>{errors[contentKey]}</small>}</label>
      </div>}
      <footer className="bcms-footer"><div><label className="bcms-switch"><input type="checkbox" checked={form.status} disabled={!canToggle} onChange={(e) => update("status", e.target.checked)} /><span /><div><strong>{t("cmsContent.editor.contentStatus")}</strong><small>{t(form.status ? "cmsContent.editor.visible" : "cmsContent.editor.currentlyHidden")}</small></div></label></div>
        {canEdit && <button className="btn bcms-save" disabled={saving || loading}>{saving ? <Spinner size="sm" /> : <Save size={17} />} {t("cmsContent.editor.saveBoth")}</button>}
      </footer>
    </form>
  </main>;
}
